import React, { useEffect, useMemo, useRef } from "react";
import { MessageTypeConstant } from "../../utils/constant";
import { isEmpty } from "lodash";
import NotificationMessages from "../common/notification-messages/NotificationMessages";
import { useMessages } from "../../hooks/common/UseMessages";
import { useTranslation } from "react-i18next";
import {openSnowModal} from '../../utils/utils';
import useCustomLocation from '../../hooks/common/UseCustomLocation';
import './assign-inventory-messages.scss';

export default function AssignInventoryMessages({
    isCharLimitError,
    isInvalidCodeError,
    onlyNumberError,
    searchError,
    isEmptySearch,
    badRequestError,
    isProduction,
    laneSummary,
    isGetPreRegistError,
    laneEligibilityCheckError,
    saveDraftVehicleMessage,
    errorMessage,
    charLimitType,
    badPayload,
    patPendingSaleTypeCount,
    patFailedEligibilityCount

}) {
    const { messages, putMessage, clearMessages, removeMessage } = useMessages();
    const { t } = useTranslation()
    const {pathName} = useCustomLocation()

    // FR-014b: dismissing a banner must keep it hidden until its underlying condition clears and
    // re-triggers — not just until the next unrelated re-render recomputes messageConditionMapping
    // and silently re-adds it. dismissedIds tracks *which occurrence* was dismissed (by condition id);
    // lastShownIdByType tracks which occurrence is currently shown for a given message type, since
    // useMessages only ever keeps one message per type.
    const dismissedIdsRef = useRef(new Set())
    const lastShownIdByTypeRef = useRef({})

    const displaySellerLaneSummary = useMemo(() => {
        const message = t(
            'MESSAGE.COMMON.INFO_MESSAGE_FOR_RESERVED_AND_REGISTERED'
        )

        let laneMessage = ''

        for (const lane in laneSummary) {
            if (laneSummary.hasOwnProperty(lane)) {
                laneMessage += 'Lane ' + lane + ': '
                laneMessage += '(' + laneSummary[lane].SP + '/'
                laneMessage += laneSummary[lane].SR + ') '
            }
        }

        return message + laneMessage
    }, [laneSummary])

    const searchEmptyErrorMsg = useMemo(() => (
        <>
            {t(
                'MESSAGE.COMMON.ERROR.ASSIGN_INVENTORY.SEARCH_DEALER.SEARCH_EMPTY_ERROR.PRE_TEXT')}
            <a href={pathName} onClick={openSnowModal}>
                {t(
                    'MESSAGE.COMMON.ERROR.ASSIGN_INVENTORY.SEARCH_DEALER.SEARCH_EMPTY_ERROR.LINK_TEXT')}
            </a>
        </>
    ), [])

    const processErrorMsg = useMemo(() => (
        <>
            {t('MESSAGE.COMMON.PROCESS_ERROR.PRE_TEXT')}
            <a href={pathName} onClick={openSnowModal}>
                {t('MESSAGE.COMMON.PROCESS_ERROR.LINK_TEXT')}
            </a>
        </>
    ), [])

    const chatLimitErrorMsg = useMemo(() =>
        t('MESSAGE.COMMON.ERROR.ASSIGN_INVENTORY.SEARCH_DEALER.CHAR_LIMIT_ERROR').replace('%TYPE%', charLimitType)
    , [charLimitType])

    const invalidCodeErrorMsg = useMemo(() =>
        t('MESSAGE.COMMON.ERROR.ASSIGN_INVENTORY.SEARCH_DEALER.INVLAID_CODE_ERROR').replace('%TYPE%', charLimitType)
    , [charLimitType])

    const numberOnlyErrorMsg = useMemo(() =>
        t('MESSAGE.COMMON.ERROR.ASSIGN_INVENTORY.SELL_NUMBER.NUMBER_ONLY')
    , [])

    const vehicleErrorMsg = useMemo(() =>
        t('MESSAGE.COMMON.ERROR.ASSIGN_INVENTORY.SEARCH_DEALER.VEHICLE_ERROR', badPayload)
    , [badPayload])

    // Change request (2026-09-07): when the lane-level PAT pre-check gets no answer at all (PAT
    // server down/timeout, not the RESOURCE_NOT_FOUND "nothing configured" business case), the
    // search is blocked entirely (see AssignInventorySearchBar.js's triggerLaneEligibilityPreCheck) -
    // this banner is the only way out, mirroring the per-vehicle "RETRY SALE TYPE CHECK" link.
    const laneEligibilityCheckErrorMsg = useMemo(() => (
        <>
            {t('Sale type eligibility check failed for this lane — please retry.')}
            {' '}
            <a
                href={'#'}
                className="font-weight-bold assign-inventory-messages__retry-link"
                onClick={(e) => { e.preventDefault(); window.triggerAssignInventorySearch && window.triggerAssignInventorySearch() }}>
                {t('RETRY')}
            </a>
        </>
    ), [])

    const infoMessageForTestingMsg = useMemo(() =>
            t('MESSAGE.COMMON.INFO_MESSAGE_FOR_TESTING')
    , [])

    const patPendingSaleTypeMsg = useMemo(() => (
        // Explicit font-size so this JSX message matches the plain-string messages above it exactly —
        // the wrapping <span>/<strong> here otherwise render at the browser/library's default size,
        // which can differ from the plain-text messages' inherited size.
        <span className="pat-pending-sale-type-message">
            <strong>{patPendingSaleTypeCount} {t(patPendingSaleTypeCount === 1 ? 'vehicle' : 'vehicles')}</strong>
            {' '}{t('have pending sale type recommendations')}
        </span>
    ), [patPendingSaleTypeCount])

    // DE758745: distinct from patPendingSaleTypeMsg above - this is an eligibility check that
    // actually failed (network/API error), not one that's merely waiting on a user selection.
    const patFailedEligibilityMsg = useMemo(() => (
        <span className="pat-pending-sale-type-message">
            <strong>{patFailedEligibilityCount} {t(patFailedEligibilityCount === 1 ? 'vehicle' : 'vehicles')}</strong>
            {' '}{t('failed sale type eligibility check — retry required before Send to AS400')}
        </span>
    ), [patFailedEligibilityCount])

    const messageConditionMapping = useMemo(() => {
        return [
            {id: 'CHAR_LIMIT_ERROR', condition: () => !!isCharLimitError, message: chatLimitErrorMsg, type: MessageTypeConstant.ERROR},
            {id: 'INVALID_CODE_ERROR', condition: () => !!isInvalidCodeError, message: invalidCodeErrorMsg, type: MessageTypeConstant.ERROR},
            {id: 'NUMBER_ONLY_ERROR', condition: () => !!onlyNumberError, message: numberOnlyErrorMsg, type: MessageTypeConstant.ERROR},
            {id: 'SEARCH_ERROR', condition: () => !!searchError, message: errorMessage, type: MessageTypeConstant.ERROR},
            {id: 'EMPTY_SEARCH_ERROR', condition: () => !!isEmptySearch, message: searchEmptyErrorMsg, type: MessageTypeConstant.ERROR},
            {id: 'BAD_REQUEST_ERROR', condition: () => !!badRequestError, message: vehicleErrorMsg, type: MessageTypeConstant.ERROR},
            {id: 'TESTING_INFO', condition: () => !isProduction() && !isEmpty(laneSummary), message: infoMessageForTestingMsg, type: MessageTypeConstant.INFO},
            {id: 'LANE_SUMMARY_INFO', condition: () => !isEmpty(laneSummary), message: displaySellerLaneSummary, type: MessageTypeConstant.INFO},
            {id: 'PRE_REGIST_ERROR', condition: () => !!isGetPreRegistError, message: processErrorMsg, type: MessageTypeConstant.ERROR},
            {id: 'LANE_ELIGIBILITY_CHECK_ERROR', condition: () => !!laneEligibilityCheckError, message: laneEligibilityCheckErrorMsg, type: MessageTypeConstant.ERROR},
            // Bug fix (2026-08-19): the "ready to send" success message is a point-in-time claim about
            // the batch as it stood right after that Save As Draft - it goes stale the moment a later
            // Edit/PAT check puts a vehicle back into "needs review"/"failed" state, so it must stop
            // showing once that happens instead of lingering alongside (or reappearing next to) the
            // WARNING/ERROR banner for the same batch. An error-type save-draft message has no such
            // staleness (it already reflects vehicles still needing attention), so only the success
            // variant is gated here.
            {
                id: 'SAVE_DRAFT_MESSAGE',
                condition: () => !!saveDraftVehicleMessage
                    && (saveDraftVehicleMessage.type !== 'success' || (!(patPendingSaleTypeCount > 0) && !(patFailedEligibilityCount > 0))),
                message: saveDraftVehicleMessage?.message,
                type: saveDraftVehicleMessage?.type
            },
            {id: 'PAT_PENDING_SALE_TYPE', condition: () => patPendingSaleTypeCount > 0, message: patPendingSaleTypeMsg, type: MessageTypeConstant.WARNING},
            {id: 'PAT_FAILED_ELIGIBILITY', condition: () => patFailedEligibilityCount > 0, message: patFailedEligibilityMsg, type: MessageTypeConstant.ERROR}
        ]
    }, [isCharLimitError, isInvalidCodeError, onlyNumberError,
        searchError, isEmptySearch, badRequestError, isProduction,
        laneSummary, isGetPreRegistError, laneEligibilityCheckError, saveDraftVehicleMessage,
        errorMessage, charLimitType, badPayload, patPendingSaleTypeCount, patFailedEligibilityCount])

    useEffect(() => {
        clearMessages()
        lastShownIdByTypeRef.current = {}
        messageConditionMapping.forEach(({id, condition, message, type}) => {
            const isActive = condition() === true
            if (!isActive) {
                // Condition cleared — a future re-trigger of this same id is a new occurrence.
                dismissedIdsRef.current.delete(id)
                return
            }
            if (dismissedIdsRef.current.has(id)) {
                return
            }
            putMessage(message, type)
            lastShownIdByTypeRef.current[type] = id
        })
    }, [messageConditionMapping])

    // FR-014b: dismissing a WARNING/ERROR banner marks its current occurrence dismissed (so the next
    // recompute of messageConditionMapping won't silently re-add it) and removes it from view now.
    // removeMessage(type) removes ALL messages of that type, not just this one occurrence - relies on
    // messageConditionMapping's invariant that at most one message per type is ever active at once
    // (PAT_PENDING_SALE_TYPE is the only WARNING, PAT_FAILED_ELIGIBILITY is the only ERROR, and the
    // other ERROR conditions above are mutually exclusive in practice). If that invariant is ever
    // broken by a future message condition, this must switch to removing by message id instead.
    function handleDismiss(type) {
        const id = lastShownIdByTypeRef.current[type]
        if (id !== undefined) {
            dismissedIdsRef.current.add(id)
        }
        removeMessage(type)
    }

    return (
        <div className="assign-inventory-messages">
            <NotificationMessages
                messages={messages}
                onWarningMessageToggleCallBack={() => handleDismiss(MessageTypeConstant.WARNING)}
                onErrorMessageToggleCall={() => handleDismiss(MessageTypeConstant.ERROR)}>
            </NotificationMessages>
        </div>
    )
}